import { useState } from 'react'

import InfiniteList from '@/common/InfiniteList'
import ResponsiveModalDrawer from '@/common/ResponsiveModalDrawer'
import ScrollToTop from '@/common/ScrollToTop'
import {
  formatAtomicCompact,
  getNftUnlockPercent,
  getNftWithdrawableAtomic,
  isMobile,
} from '@/helpers'
import { cn } from '@/theme/utils'
import { Nft } from '@/types'
import { UiCard } from '@/ui/UiCard'
import UiContainer from '@/ui/UiContainer'
import { UiSkeleton } from '@/ui/UiSkeleton'

import { ActiveModal, NftActionModals } from './components/NftActionModals'
import {
  NftDetailsModalContent,
  NftHeader,
} from './components/NftDetailsModalContent'
import NftListHeader from './components/NftListHeader'
import { useNftListViewModel } from './hooks/nft-list-view-model'
import { useNftMetadata } from './hooks/nft-metadata'

export default function NftList() {
  const vm = useNftListViewModel()
  const [activeModal, setActiveModal] = useState<ActiveModal>(null)

  const renderDetailsHeader = () => {
    if (!vm.selectedNft) return null
    return (
      <NftHeader
        canTopUp={vm.actions.visible && vm.actions.canTopUp}
        canClose={vm.isModuleAdmin}
        uri={vm.selectedNft.uri}
        vestingTimeLabel={vm.vestingTimeLabel}
        availableToWithdrawAtomic={vm.selectedNftWithdrawableAtomic}
        nftBalanceAtomic={vm.selectedNftBalanceAtomic}
        actions={vm.actions}
        onTopUp={() => setActiveModal('top-up')}
        onCloseShard={() => setActiveModal('close')}
        onDelegate={() => setActiveModal('delegate')}
        onUndelegate={() => setActiveModal('undelegate')}
        onRedelegate={() => setActiveModal('redelegate')}
        onWithdraw={() => setActiveModal('withdraw')}
        onChangeOwner={() => setActiveModal('change-owner')}
      />
    )
  }

  if (vm.isLoading) {
    return <NftListLoadingSkeleton />
  }

  // Only show full-page error on initial load. If pagination fails, keep the list visible.
  if (vm.isError && vm.nfts.length === 0) {
    return (
      <div className='text-foreground mx-auto mt-37.5 rounded-lg p-6 text-center lg:max-w-2/5'>
        <p className='text-destructive text-2xl font-semibold'>
          Failed to load Shards
        </p>
        <p className='muted-foreground mt-3'>
          There is an issue while loading Shards. Please try again later.
        </p>
      </div>
    )
  }

  return (
    <>
      <UiContainer className='mt-15 py-6'>
        <NftListHeader
          searchQuery={vm.searchQuery}
          onSearchChange={vm.setSearchQuery}
          showMineOnly={vm.showMineOnly}
          onToggleMineOnly={vm.toggleMineOnly}
          isWalletConnected={vm.isWalletConnected}
          isModuleAdmin={vm.isModuleAdmin}
          isValidator={vm.isValidator}
          isJailed={vm.isJailed}
          onMintOpen={() => setActiveModal('mint')}
          onBecomeOpen={() => vm.setBecomeChoiceOpen(true)}
          onUnjailOpen={() => setActiveModal('unjail')}
        />

        <InfiniteList
          items={vm.filteredItems}
          loadingState={vm.listLoadingState}
          hasNextPage={vm.hasNextPage}
          noDataTitle={
            vm.showMineOnly
              ? 'No Shards found for your wallet'
              : 'No Shards found'
          }
          noDataDescription={
            vm.showMineOnly
              ? 'Mint or acquire Shards owned by your connected wallet, then refresh.'
              : 'Try minting some, then refresh.'
          }
          onRetry={vm.refetch}
          onLoadNext={vm.loadNextPage}
        >
          {vm.filteredItems.length === 0 && vm.nfts.length > 0 && (
            <NoShardsFoundState showMineOnly={vm.showMineOnly} />
          )}

          <div className='grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4'>
            {vm.filteredItems.map((nft, idx) => (
              <NftCard
                key={nft.address}
                nft={nft}
                index={idx}
                vestingTimeLabel={vm.vestingTimeLabel}
                onSelect={vm.selectNft}
              />
            ))}
          </div>
        </InfiniteList>

        <ResponsiveModalDrawer
          open={!!vm.selectedNft}
          onOpenChange={isOpen => {
            if (!isOpen) vm.closeNftModal()
          }}
          title={!isMobile() && vm.selectedNft ? renderDetailsHeader() : null}
          content={
            vm.selectedNft && (
              <>
                {isMobile() && (
                  <div className='mb-4'>{renderDetailsHeader()}</div>
                )}

                <NftDetailsModalContent
                  currentBlock={vm.currentBlock}
                  vestingPeriodBlocks={vm.vestingPeriodBlocks}
                  nft={vm.selectedNft}
                  delegation={vm.selectedDelegationInfo}
                  isDelegationLoading={vm.isLoadingSelectedDelegation}
                />
              </>
            )
          }
          headerWrapperClassName='md:bg-popover pt-3 md:p-3 rounded-t-2xl border-b border-border'
          headerClassName='space-y-0 px-4'
          scrollAreaClassName='p-6 pt-4'
          drawerProps={{ className: 'bg-card rounded-lg' }}
          modalProps={{
            className: 'bg-card rounded-lg grad max-w-3xl p-0 gap-0',
          }}
        />

        <NftActionModals
          activeModal={activeModal}
          setActiveModal={setActiveModal}
          nftAddress={vm.selectedNft?.address}
          becomeChoiceOpen={vm.becomeChoiceOpen}
          setBecomeChoiceOpen={vm.setBecomeChoiceOpen}
        />
      </UiContainer>

      <ScrollToTop threshold={300} />
    </>
  )
}

function NftCard({
  nft,
  index,
  vestingTimeLabel,
  onSelect,
}: {
  nft: Nft
  index: number
  vestingTimeLabel?: string | null
  onSelect: (nftAddress: string) => void
}) {
  const { data: metadata } = useNftMetadata(nft.uri)
  const placeholderImageSrc = `${import.meta.env.BASE_URL}images/nft-preview.png`
  const available = formatAtomicCompact(getNftWithdrawableAtomic(nft))
  const unlockPercent = getNftUnlockPercent(nft)

  return (
    <UiCard
      className={cn(
        'relative overflow-hidden rounded-2xl',
        'bg-card/35 backdrop-blur-md',
        'border-gradient',
        'hover:bg-card/45 transition-colors',
      )}
      onClick={() => onSelect(nft.address)}
    >
      {vestingTimeLabel && (
        <div
          className={cn(
            'pointer-events-none absolute top-4 left-4 z-10 flex items-center gap-2',
          )}
        >
          <div
            className={cn(
              'chip-gradient text-foreground inline-flex h-7 items-center rounded-full px-3 text-xs font-semibold backdrop-blur-md',
            )}
          >
            {vestingTimeLabel}
          </div>
          <div
            className={cn(
              'chip-gradient-teal text-foreground inline-flex h-7 items-center rounded-full px-3 text-xs font-semibold backdrop-blur-md',
            )}
          >
            +20%
          </div>
        </div>
      )}
      <div className='p-1'>
        <div className='flex aspect-square w-full items-center justify-center'>
          <img
            className='h-full w-full rounded-[11px] object-cover'
            src={placeholderImageSrc}
            alt={metadata?.name ?? `NFT ${index + 1}`}
          />
        </div>
      </div>
      <div className='px-5 pb-5'>
        <div className='mt-3 flex items-center justify-between gap-3'>
          <div className='min-w-0'>
            <div className='max-w-43.75 truncate text-[18px] font-semibold'>
              {metadata?.name || `Unknown NFT`}
            </div>
          </div>
          <div className='shrink-0 rounded-lg text-sm font-bold text-[#16D1D6]'>
            {available} BRIDGE
          </div>
        </div>
        <div className='pt-4'>
          <div className='grid grid-cols-2 gap-3'>
            <div>
              <span className='text-muted-foreground text-xs'>
                Reward per period
              </span>
              <div className='mt-1 text-sm font-semibold'>
                {formatAtomicCompact(nft.reward_per_period.amount)} BRIDGE
              </div>
            </div>
            <div className='ml-auto text-right'>
              <span className='text-muted-foreground text-xs'>Unlocked</span>
              <div className='mt-1 text-sm font-semibold'>{unlockPercent}%</div>
            </div>
          </div>
        </div>
      </div>
    </UiCard>
  )
}

function NoShardsFoundState({ showMineOnly }: { showMineOnly: boolean }) {
  return (
    <UiCard className='text-foreground mx-auto mt-37.5 rounded-lg p-6 text-center lg:max-w-2/5'>
      <p className='text-2xl font-semibold'>
        {showMineOnly ? 'No Shards found for your wallet' : 'No Shards found'}
      </p>
      <p className='muted-foreground mt-3'>
        {showMineOnly
          ? 'Mint or acquire Shards owned by your connected wallet, then refresh.'
          : 'Try minting some, then refresh.'}
      </p>
    </UiCard>
  )
}

function NftListLoadingSkeleton() {
  return (
    <UiContainer className='mt-15 py-6'>
      {/* Header Skeleton */}
      <div className='mb-4 grid grid-cols-2 gap-3 px-4 py-4 sm:grid-cols-2 sm:px-0 sm:py-5 md:gap-4 md:px-0'>
        <div className='flex flex-col gap-3'>
          <UiSkeleton className='h-8 w-40 rounded-md' />
          <UiSkeleton className='h-11.5 w-full max-w-103.75 rounded-lg' />
        </div>
        <div className='flex flex-col justify-between gap-3'>
          <div className='flex items-center justify-end gap-2'>
            <UiSkeleton className='h-4 w-24 rounded-md' />
            <UiSkeleton className='h-6 w-11 rounded-full' />
          </div>
          <div className='flex flex-col items-center gap-2 sm:flex-row sm:justify-end sm:gap-3'>
            <UiSkeleton className='h-9 w-full max-w-40 rounded-full' />
          </div>
        </div>
      </div>

      {/* Grid Skeleton */}
      <div className='grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4'>
        {Array.from({ length: 8 }).map((_, index) => (
          <NftCardSkeleton key={`nft-skeleton-${index}`} />
        ))}
      </div>
    </UiContainer>
  )
}

function NftCardSkeleton() {
  return (
    <UiCard
      className={cn(
        'bg-card/35 border-gradient relative overflow-hidden rounded-2xl backdrop-blur-md',
      )}
    >
      <UiSkeleton className='absolute top-4 left-4 h-7 w-20 rounded-full' />
      <div className='p-3'>
        <div className='flex aspect-square w-full items-center justify-center'>
          <UiSkeleton className='h-full w-full rounded-md' />
        </div>
      </div>
      <div className='px-4 pb-4'>
        <div className='flex items-center justify-between gap-3'>
          <div className='min-w-0'>
            <UiSkeleton className='h-4 w-32' />
          </div>
          <UiSkeleton className='h-6 w-24 rounded-md' />
        </div>
        <div className='mt-4 border-t border-white/10 pt-4'>
          <div className='grid grid-cols-2 gap-3'>
            <div>
              <UiSkeleton className='mb-2 h-3 w-20' />
              <UiSkeleton className='h-4 w-28' />
            </div>
            <div className='ml-auto text-right'>
              <UiSkeleton className='mb-2 ml-auto h-3 w-16' />
              <UiSkeleton className='ml-auto h-4 w-16' />
            </div>
          </div>
        </div>
      </div>
    </UiCard>
  )
}
